import { classLogin, getClassSchools, getClassLoginQRCode, getLoginQRCode, getSchools, getSchoolSid, isGoSchool as apiIsGoSchool, login as legacyLogin, pollingClassLogin, pollingLogin } from "@/utils/api";
import { API_MODE_CHANGED_EVENT, getApiMode, getApiModeOption } from "@/utils/apiMode";
import { useToast } from "vue-toastification";
import { ElSelect, ElOption, ElInput, ElButton } from "element-plus";
export default {
  components: {
    ElSelect,
    ElOption,
    ElInput,
    ElButton
  },
  setup() {
    const toast = useToast();
    return {
      toast
    };
  },
  data() {
    return {
      username: "",
      password: "",
      error: "",
      qrCodeUrl: "",
      pollingTimer: null,
      selectedSchool: "",
      schoolList: [],
      apiMode: getApiMode(),
      loginLoading: false
    };
  },
  computed: {
    selectedSchoolInfo() {
      return this.schoolList.find(school => school.email === this.selectedSchool);
    },
    apiModeOption() {
      return getApiModeOption(this.apiMode);
    }
  },
  async created() {
    // Always load schools immediately. Headless/Electron background windows may
    // report a non-visible document state, and delaying this request leaves the
    // login form without selectable schools.
    await this.fetchSchools();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener(API_MODE_CHANGED_EVENT, this.handleApiModeSync);
  },
  beforeUnmount() {
    this.clearPolling();
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener(API_MODE_CHANGED_EVENT, this.handleApiModeSync);
  },
  methods: {
    isGoSchool(school) {
      return this.shouldUseClassApi(school);
    },
    shouldUseClassApi(school) {
      if (this.apiMode === "class") return true;
      if (this.apiMode === "web" || this.apiMode === "ios") return false;
      return apiIsGoSchool(school);
    },
    schoolPlatformLabel(school) {
      if (this.shouldUseClassApi(school)) return "Class";
      if (this.apiMode === "web" || this.apiMode === "ios") return "Web";
      return "";
    },
    goSettings() {
      window.location.hash = "#/settings";
    },
    async handleApiModeSync(event) {
      const nextMode = event?.detail?.mode || getApiMode();
      if (nextMode === this.apiMode) return;
      this.apiMode = nextMode;
      this.selectedSchool = "";
      this.schoolList = [];
      this.qrCodeUrl = "";
      this.clearPolling();
      await this.fetchSchools();
    },
    async handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        if (!this.schoolList.length) await this.fetchSchools();
        if (this.selectedSchool) await this.startQRCodePolling();
      }
    },
    async loadQRCodeAndStartPolling() {
      await this.fetchSchools();
      await this.startQRCodePolling();
    },
    getSelectedSchool() {
      return this.schoolList.find(school => school.email === this.selectedSchool);
    },
    async handleSchoolChange() {
      await this.startQRCodePolling();
    },
    clearPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },
    async startQRCodePolling() {
      this.clearPolling();
      this.qrCodeUrl = "";
      const selectedSchool = this.getSelectedSchool();
      if (!selectedSchool) return;
      try {
        const classSchool = this.shouldUseClassApi(selectedSchool);
        const response = classSchool ? await getClassLoginQRCode(getSchoolSid(selectedSchool)) : await getLoginQRCode();
        if (response.code === 0) {
          this.qrCodeUrl = classSchool ? `data:image/png;base64,${response.data.url}` : response.content.dataUrl;
          const token = classSchool ? response.data.qrcodeId : response.content.token;
          this.pollingTimer = setInterval(async () => {
            // 检测当前URL是否为 #/login
            if (window.location.hash !== "#/login") {
              // 如果不是 #/login，则停止轮询
              this.clearPolling();
              return;
            }
            try {
              const result = classSchool ? await pollingClassLogin(token, selectedSchool) : await pollingLogin(token);
              if (result.code === 0 && (result.message === "success" || result.msg === "成功" || result.content)) {
                // 处理登录成功逻辑
                console.log("登录成功");
                this.toast.success("登录成功", {
                  position: "top-center"
                });
                // 保存登录状态和用户信息到本地存储
                localStorage.setItem("loggedIn", "true");
                window.dispatchEvent(new Event("pu-auth-changed"));
                localStorage.setItem("userData", JSON.stringify(result.content));
                window.location.hash = "#/";
                this.clearPolling();
              } else if (result.code === 2 && result.message === "token失效") {
                // 处理token失效逻辑
                console.log("token失效");
                await this.startQRCodePolling();
              }
            } catch (error) {
              console.error(error);
            }
          }, 1500);
        } else {
          console.error(response.message);
          this.toast.error("获取二维码失败，请重试。", {
            position: "top-center"
          });
        }
      } catch (error) {
        console.error(error);
        this.toast.error("获取二维码失败，请检查网络连接。", {
          position: "top-center"
        });
      }
    },
    normalizeClassSchool(school) {
      return {
        ...school,
        email: `class:${school.id}`,
        go_id: school.id,
        is_go: 1,
        apiPlatform: "class"
      };
    },
    async fetchSchools() {
      try {
        const loadLegacySchools = this.apiMode !== "class";
        const loadClassSchools = this.apiMode !== "web" && this.apiMode !== "ios";
        const [legacyResult, classResult] = await Promise.allSettled([loadLegacySchools ? getSchools({
          silent: true
        }) : Promise.resolve([]), loadClassSchools ? getClassSchools() : Promise.resolve({
          data: {
            list: []
          }
        })]);
        const legacySchools = legacyResult.status === "fulfilled" && Array.isArray(legacyResult.value) ? legacyResult.value.map(school => ({
          ...school,
          apiPlatform: "web"
        })) : [];
        const classSchools = classResult.status === "fulfilled" ? (classResult.value?.data?.list || []).map(this.normalizeClassSchool) : [];
        let schools = [];
        if (this.apiMode === "class") {
          schools = classSchools;
        } else if (this.apiMode === "web" || this.apiMode === "ios") {
          schools = legacySchools;
        } else {
          const merged = new Map();
          legacySchools.forEach(school => {
            merged.set(`${school.name}:${school.email || school.id || ""}`, school);
          });
          classSchools.forEach(school => {
            const legacyMatch = [...merged.values()].find(item => item.name === school.name);
            if (legacyMatch) {
              legacyMatch.go_id = legacyMatch.go_id || school.id;
              legacyMatch.is_go = legacyMatch.is_go ?? 1;
              return;
            }
            merged.set(`${school.name}:${school.id}`, school);
          });
          schools = [...merged.values()];
        }
        this.schoolList = schools.sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "zh-Hans-CN"));
        if (!this.schoolList.length) {
          throw new Error("学校列表为空");
        }
      } catch (error) {
        console.error(error);
        this.toast.error("获取学校列表失败，请检查网络连接。", {
          position: "top-center"
        });
      }
    },
    async login() {
      if (this.loginLoading) return;
      this.loginLoading = true;
      try {
        try {
          const selectedSchool = this.schoolList.find(school => school.email === this.selectedSchool);
          if (!selectedSchool) {
            this.toast.error("请选择学校", {
              position: "top-center"
            });
            return;
          }
          if (this.shouldUseClassApi(selectedSchool)) {
            const response = await classLogin(this.username, this.password, getSchoolSid(selectedSchool), selectedSchool);
            if (response.code === 0) {
              console.log("登录成功");
              this.toast.success("登录成功", {
                position: "top-center"
              });
              localStorage.setItem("loggedIn", "true");
              window.dispatchEvent(new Event("pu-auth-changed"));
              localStorage.setItem("userData", JSON.stringify(response.content));
              window.location.hash = "#/";
            } else {
              this.toast.error(response.msg || response.message || "登录失败", {
                position: "top-center"
              });
            }
            return;
          }
          const usernameWithSchool = this.username + selectedSchool.email;
          const response = await legacyLogin(usernameWithSchool, "pc", this.password, this.username, "", selectedSchool.email);
          if (response.message === "success") {
            console.log("登录成功");
            this.toast.success("登录成功", {
              position: "top-center"
            });
            localStorage.setItem("loggedIn", "true");
            window.dispatchEvent(new Event("pu-auth-changed"));
            localStorage.setItem("userData", JSON.stringify(response.content));
            window.location.hash = "#/";
          } else {
            console.log(response.message);
            this.toast.error(response.message, {
              position: "top-center"
            });
          }
        } catch (error) {
          console.error(error);
          this.toast.error("登录失败，请检查用户名和密码。", {
            position: "top-center"
          });
        }
      } finally {
        this.loginLoading = false;
      }
    }
  }
};